import { useEffect, useState } from "react"

import { cn } from "@productlane/lib/src/cn"
import { Sheet, SheetContent, SheetTitle } from "@productlane/ui"

import type { DetailDrawerTarget } from "@/lib/detail-drawer"
import { useDetailDrawer, useDetailDrawerTarget } from "@/lib/detail-drawer"
import { IssueDetailView } from "@/screens/app/issues/detail-screen"
import { ProjectDetailView } from "@/screens/app/projects/detail-screen"

export const DetailDrawer = () => {
	const target = useDetailDrawerTarget()
	const { close } = useDetailDrawer()

	// Hold the last target so the panel keeps its content while it slides out.
	const [rendered, setRendered] = useState<DetailDrawerTarget | null>(target)
	useEffect(() => {
		if (target) setRendered(target)
	}, [target])

	return (
		<Sheet
			open={!!target}
			onOpenChange={(open) => {
				if (!open) close()
			}}
		>
			{rendered && (
				<SheetContent
					side="right"
					hideCloseButton
					isOverlayVisible
					overlayClassName={cn(
						"detail-drawer-overlay",
						"bg-gray-950/20 backdrop-blur-none dark:bg-gray-950/50",
					)}
					aria-describedby={undefined}
					className={cn(
						"detail-drawer",
						"flex w-full flex-col gap-0 border-l-0 p-0 sm:max-w-[52rem]",
						// Same card shell as the main content pane, hugging the right edge:
						// rounded and ringed on the left, flush and square on the right.
						"inset-y-1 h-auto overflow-hidden rounded-l-2xl lg:inset-y-2",
						"shadow-xl ring-1 ring-black/10 dark:shadow-black dark:ring-white/15",
						"transition-none",
					)}
					onOpenAutoFocus={(e) => e.preventDefault()}
				>
					<SheetTitle className="sr-only">
						{rendered.type === "issue" ? "Issue detail" : "Project detail"}
					</SheetTitle>
					{rendered.type === "issue" ? (
						<IssueDetailView
							key={rendered.id}
							id={rendered.id}
							variant="drawer"
							onClose={close}
						/>
					) : (
						<ProjectDetailView
							key={rendered.id}
							id={rendered.id}
							variant="drawer"
							onClose={close}
						/>
					)}
				</SheetContent>
			)}
		</Sheet>
	)
}
